/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import _ from 'lodash';
import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import isUndefined from 'lodash.isundefined';
import {setRel} from '@ion/core/src/services/ConnectUtils';
import {
  Account,
  Device,
  DeviceType,
  EntityStatus,
  GenericObject,
  Link,
  MDTDeviceTags,
  OEMDeviceTags,
  SCIDeviceTags
} from '@ion/core/src/types/entities';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {isValue} from '@ion/core/src/utils/StringUtil';
import {AugmentedFormState} from '@ion/dls/src/hooks/useFormState';
import {
  convertStatus,
  dateTimeFormatter
} from '@ion/dls/src/utils/ComponentUtils';
import {
  getLastAddress,
  getLastReportedDate
} from '@ion/dls/src/utils/GridUtils';
import {parseAttributes} from './GroupEditorUtils';

// device fields included in calls to the CTC API ...
export enum DeviceFields {
  Id = 'id',
  DeviceName = 'name',
  Account = 'account',
  Status = 'status'
}

// available field names for the add/edit device form
export enum DeviceFormFields {
  Account = 'account',
  DeviceName = 'name',
  Status = 'status',
  Spreader = 'Spreader',
  Features = 'features',
  DeviceType = 'type',
  DeviceConfiguration = 'deviceConfiguration',
  ESN = 'esn',
  ICCID = 'iccid',
  ShipDate = 'shipDate',
  ISDN = 'isdn',
  AirId = 'airId',
  MEID = 'meid',
  IMEI = 'imei',
  IMEI2 = 'imei2',
  DeviceNote = 'deviceNote',
  IMSI = 'imsi',
  MDN = 'mdn',
  ExternalId ='externalId',
  IPAddress = 'ipAddress',
  CarrierStatus = 'carrierStatus',
  Asset = 'asset',
  SerialNumber = 'serialNumber',
  BLE = 'ble',
  ICN = 'icn',
  IVT = 'ivt',
  ATTACHMENTS = 'ATTACHMENTS',
  Version = 'version',
  Group = 'group',
  Groups = 'groups',
  CreatedOn = 'createdOnUI',
  LastModifiedOn = 'lastModifiedOnUI',
  LastReported = 'lastReportedUI',
  DeviceId = 'deviceIdUI',
  LastAddress = 'lastAddress',
  ExtendedAttribute = 'extendedAttribute'
};

export enum SCIDeviceSettingsFormField {
  GPSActivated = 'gpsActivated',
  iOnTagEnabled = 'iOnTagEnabled',
  autoFlightMode = 'autoFlightMode',
  pressureSensorFlightMode = 'pressureSensorFlightMode',
  oceanMode = 'oceanMode',
  MoveWakeupPeriod = 'moveWakeupPeriod',
  WakeupPeriod = 'wakeupPeriod',
  SensorLoggingPeriod = 'sensorLoggingPeriod',
  TemperatureLogging = 'temperatureLogging',
  MinTemperature = 'minTemperature',
  MaxTemperature = 'maxTemperature',
  LightLevelAlert = 'lightLevelAlert', MinLightLevel = 'minLightLevel',
  MaxLightLevel = 'maxLightLevel',
  TipAlert = 'tipAlert',
  MaxTip = 'maxTip',
  ImpactAlert = 'impactAlert',
  MaxImpact = 'maxImpact',
  TemperatureAlert = 'temperatureAlert',
};

export enum SCIDeviceSettingsUnits {
  Degree = '°',
  Shock = 'G',
  Light = 'lm'
};

export enum EOLTypes {
  NotSupported = 'not supported',
  Supported = 'supported'
};

export interface SCIDeviceSettingsForm {
  gpsActivated?: boolean;
  iOnTagEnabled?: boolean;
  pressureSensorFlightMode?: boolean;
  autoFlightMode?: boolean;
  oceanMode?: boolean;
  moveWakeupPeriod?: number;
  wakeupPeriod?: number;
  sensorLoggingPeriod?: number;
  temperatureLogging?: boolean;
  minTemperature?: number;
  maxTemperature?: number;
  lightLevelAlert?: boolean;
  minLightLevel?: number;
  maxLightLevel?: number;
  tipAlert?: boolean;
  maxTip?: number;
  impactAlert?: boolean;
  maxImpact?: boolean;
  temperatureAlert?: boolean;
};

export type SerializedArgs = {
  state: AugmentedFormState;
  account?: Account | Link;
  isEditMode?: boolean;
};

export interface TargetType {
  name: DeviceFormFields,
  value: string;
  required?: boolean;
  error?: boolean;
  changed?: boolean;
}

export const getSpreadVal = (value: string): string => {
  if (isValue('None', value)) {
    return '';
  }
  return value;
};

export interface TemperatureControlProps {
  isNumber: boolean;
  isFahrenheit: boolean;
}

const required = {required: true};
const notRequired = {required: false};

/**
 * Provides the initial state of the Device editor form, this state includes all named
 * editor fields and their requirements, as well as any initial values required, such
 * as the type for which the device is to be created, or a reference to a device,
 * in the case of edits.
 */
export const getInitialState = (requireDeviceFields: boolean, device: Device = {}, includeSwapDevice: boolean) => {
  return {
    [DeviceFormFields.Account]             : !requireDeviceFields ? required : notRequired,
    [DeviceFormFields.DeviceType]          : requireDeviceFields ? required : notRequired,
    [DeviceFormFields.DeviceConfiguration] : requireDeviceFields ? required : notRequired,
    [DeviceFormFields.Status]              : {...required, value: EntityStatus.Enabled},
    [DeviceFormFields.CarrierStatus]       : {value: 'UNKNOWN'},
    [DeviceFormFields.ESN]                 : notRequired,
    [DeviceFormFields.SerialNumber]        : notRequired,
    [DeviceFormFields.CreatedOn]           : notRequired,
    [DeviceFormFields.LastModifiedOn]      : notRequired,
    [DeviceFormFields.DeviceName]          : required,
    [DeviceFormFields.DeviceNote]          : notRequired,
    ...deserialize(device, includeSwapDevice)
  };
};

/**
 * This function takes the form data from the device form and
 * serializes it into a a CTC Device object that gets passed to the
 * addDevice service call.
 *
 * @param {object} state: The form data that needs to be transformed into
 *   an device that can be passed to ctc to create the device.
 */
export const serialize = ({state, account, isEditMode}: SerializedArgs) => {
  const serialized: GenericObject = {
    extendedAttribute : {
      value : ''
    }
  };
  const extendedAttributeValue = {};
  for (const [key, {value, changed}] of Object.entries(state)) {
    switch (key) {
      case DeviceFormFields.Account:
        serialized.account = !value?.href ? setRel(account, 'account') : setRel(value, 'account');
        break;
      case DeviceFormFields.Status:
        serialized[key] = convertStatus(value);
        break;
      case DeviceFormFields.Spreader:
        serialized[DeviceFormFields.Features] = {...value, [DeviceFormFields.ATTACHMENTS]: getSpreadVal(value)};
        break;
      case DeviceFormFields.DeviceType:
        serialized[key] = value?.id ? setRel(value, 'devicetype') : (value?.href ? value : null);
        break;
      case DeviceFormFields.DeviceConfiguration:
        serialized[key] = value?.id ? setRel(value, 'deviceConfiguration') : (value?.href ? value : null);
        break;
      case DeviceFormFields.DeviceNote:
        if (!isEmpty(value)) {
          extendedAttributeValue[key] = value;
        }
        break;
      case SCIDeviceSettingsFormField.GPSActivated:
        serialized[key] = changed ? !value : undefined;
        break;
      case SCIDeviceSettingsFormField.oceanMode:
      case SCIDeviceSettingsFormField.iOnTagEnabled:
      case SCIDeviceSettingsFormField.pressureSensorFlightMode:
      case SCIDeviceSettingsFormField.autoFlightMode:
      case SCIDeviceSettingsFormField.TemperatureLogging:
      case SCIDeviceSettingsFormField.TemperatureAlert:
      case SCIDeviceSettingsFormField.LightLevelAlert:
      case SCIDeviceSettingsFormField.ImpactAlert:
      case SCIDeviceSettingsFormField.TipAlert:
      case SCIDeviceSettingsFormField.MoveWakeupPeriod:
      case SCIDeviceSettingsFormField.WakeupPeriod:
      case SCIDeviceSettingsFormField.SensorLoggingPeriod:
      case SCIDeviceSettingsFormField.MinTemperature:
      case SCIDeviceSettingsFormField.MaxTemperature:
      case SCIDeviceSettingsFormField.MinLightLevel:
      case SCIDeviceSettingsFormField.MaxLightLevel:
      case SCIDeviceSettingsFormField.MaxTip:
      case SCIDeviceSettingsFormField.MaxImpact:
        serialized[key] = changed ? value : undefined;
        break;
      default:
        if (isUndefined(value) || value === '') {
          serialized[key] = null;
        } else {
          serialized[key] = value;
        }
        break;
    }
  }
  if (Object.keys(extendedAttributeValue).length) {
    serialized.extendedAttribute.value = safeJSONStringify({
      ...(state?.extendedAttribute?.value || {}),
      ...extendedAttributeValue
    });
  }
  delete serialized.expanded;
  if (!isEditMode) {
    delete serialized.account.subAccounts;
  }
  return serialized;
};

/**
 * creates a hashMap for the deserialized SCI device settings.
 */
const SCIDeviceDeserializeFactory = (device: Device) => {
  const SCIDeviceSettings = Object.values(SCIDeviceSettingsFormField);
  const formData = {};
  SCIDeviceSettings.forEach(name => {
    let setting;
    switch (name) {
      case SCIDeviceSettingsFormField.GPSActivated:
        setting = getSCISetting(device, 'disableGPS', true);
        break;
      case SCIDeviceSettingsFormField.iOnTagEnabled:
        setting = getSCISetting(device, 'zMicroDetection');
        break;
      case SCIDeviceSettingsFormField.oceanMode:
        setting = getSCISetting(device, SCIDeviceSettingsFormField.oceanMode);
        break;
      case SCIDeviceSettingsFormField.pressureSensorFlightMode:
        setting = getSCISetting(device, SCIDeviceSettingsFormField.pressureSensorFlightMode);
        break;
      case SCIDeviceSettingsFormField.autoFlightMode:
        setting = getSCISetting(device, SCIDeviceSettingsFormField.autoFlightMode);
        break;

      default:
        setting = getSCISetting(device, name);
    };
    formData[name] = {
      name,
      value    : setting?.value,
      pending  : setting?.pending,
      required : false
    };
  });
  return formData;
};

/**
 * This function takes a CTC account and serializes it into a device form
 * consumable object that can be pre-populated into the form. Essentially,
 * it's the reverse of the serialize function above.
 *
 * @param {Device} device: CTC account that is to be deserialized into a device form object.
 */
export const deserialize = (device: Device | GenericObject, includeSwapDevice: boolean) => {
  if (Object.keys(device || {}).length && device?.id) {
    const formData = {
      [DeviceFormFields.CreatedOn] : {
        name     : DeviceFormFields.CreatedOn,
        value    : dateTimeFormatter(device.createdOn),
        required : false
      },
      [DeviceFormFields.LastModifiedOn] : {
        name     : DeviceFormFields.LastModifiedOn,
        value    : dateTimeFormatter(device.lastModifiedOn),
        required : false
      },
      [DeviceFormFields.LastReported] : {
        name     : DeviceFormFields.LastReported,
        value    : getLastReportedDate(device, [], dateTimeFormatter),
        required : false
      },
      [DeviceFormFields.DeviceId] : {
        name     : DeviceFormFields.DeviceId,
        value    : device.esn || device.serialNumber,
        required : false
      },
      [DeviceFormFields.LastAddress] : {
        name     : DeviceFormFields.LastAddress,
        value    : getLastAddress(device),
        required : false
      },
      ...SCIDeviceDeserializeFactory(device)
    };
    for (const [key, value] of Object.entries(device)) {
      if (includeSwapDevice && key === DeviceFormFields.ESN) {
        formData[key] = {key, value: ''};
      } else if (key === DeviceFormFields.ExtendedAttribute) {
        value && parseAttributes(value, formData);
      } else {
        formData[key] = {key, value};
      }
    }
    return formData;
  } else {
    return {};
  }
};

const getSCISetting = (device: Device, setting: String, invert: Boolean = false) => {
  const propPendingStr = `sciDeviceSettings.pendingSettings.${setting}`;
  const propStr = `sciDeviceSettings.settings.${setting}`;

  if (_.has(device, propPendingStr)) {
    const val = _.get(device, propPendingStr);
    return {
      value   : invert ? !val : val,
      pending : true
    };
  }

  if (_.has(device, propStr)) {
    const val = _.get(device, propStr);
    return {
      value   : invert ? !val : val,
      pending : false
    };
  }
};

export const VisionDeviceType = 'VISION CAMERA';
const TagTypes = ['SC1004', 'SC1004V', 'SC1204', 'SC1204S', 'SC1204V', 'SC1205V'];
const SCIMovingDeviceTypes = [
  'SC1004',
  'SC1004V',
  'SC1102',
  'SC1204',
  'SC1204V',
  'SC1204S',
  'SC1205',
  'SC1205V',
  'SC1302'];
const GPSTypes                   = [...TagTypes, 'SC1102', 'SC1302'];
export const FlighTypes          = [...TagTypes];
export const OceanModeTypes      = [...TagTypes, 'SC1102', 'SC1302'];
const SCITemperatureDeviceTypes  = [...TagTypes];
export const AutoFlightModeTypes = ['SC1102', 'SC1302'];

const findDeviceMode = (device: Device, modes: string[]) => {
  const {typeName, type} = device || {};
  return modes.some(mode => mode.toLowerCase() === (typeName || type?.title || '').toLowerCase());
};

export const isFightModeDevice = (device: Device) => findDeviceMode(device, FlighTypes);

export const isOceanModeDevice = (device: Device) => findDeviceMode(device, OceanModeTypes);

export const isAutoFlightMode = (device: Device) => findDeviceMode(device, AutoFlightModeTypes);

export const isSCIGPSDevice = (device: Device) => findDeviceMode(device, GPSTypes);

export const isSCITagDevice = (device: Device) => findDeviceMode(device, TagTypes);

export const isSCITemperatureDevice = (device: Device) => findDeviceMode(device, SCITemperatureDeviceTypes);

export const isSCIMovingDevice = (device: Device) => findDeviceMode(device, SCIMovingDeviceTypes);

const isDeviceArchitecture = ({architecture = ''}: Device = {}, ...tags): boolean => (
  !!tags.find(tag => architecture === tag)
);

/**
 * Checks if device is SCI device
 */
export const isSelectedDeviceSCI = (device: Device): boolean => (
  device?.architecture && isDeviceArchitecture(device, SCIDeviceTags.SCI, SCIDeviceTags.TAG)
);

/**
 * Checks if device is CMF device
 */
export const isCMFDevice = (device: Device): boolean => (
  device?.architecture && isDeviceArchitecture(device, MDTDeviceTags.CMF)
);

/**
 * Checks if device is CMF or SCI device
 */
export const isSCIorCMFDevice = (device: Device): boolean => (
  isSelectedDeviceSCI(device) || isCMFDevice(device)
);

/**
 * Checks if device is Vision device
 */
export const isVisionDevice = (device: Device | DeviceType): boolean => {
  const typeName = device?.name || device?.type?.title || '';
  return isEqual(VisionDeviceType, typeName.toUpperCase());
};

export const checkEOLDevice = (device: Device, deviceAppIds: GenericObject[]) => {
  const appId = Number(device?.extensions?.appId) || null;
  if (appId && deviceAppIds?.length) {
    const platformObj = deviceAppIds?.find(item => Number(item?.value) === appId);
    if (platformObj) {
      const eolObj = platformObj?.platformParamValues?.find(({platformTypeParam}) => {
        return platformTypeParam?.name === EOLTypes.Supported;
      });
      return eolObj?.value === 'false';
    }
  }
  return false;
};

/**
 * Checks if device is OEM device
 */
export const isSelectedDeviceOEM = (device: Device): boolean => (
  device?.category === OEMDeviceTags.OEM
);
